import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { User } from "../../store/slice/AuthSlice";
import {
  Box,
  Fab,
  Tooltip,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  Collapse,
  useTheme,
  Badge,
  Divider,
  Zoom,
} from "@mui/material";
import {
  AutoAwesome,
  Close,
  Send,
  Minimize,
  ElectricBoltOutlined,
  EventNoteOutlined,
  AnalyticsOutlined,
  East,
  SensorsOutlined,
  ChatBubbleOutline,
  StorageOutlined,
  SearchOutlined,
  PhoneInTalkOutlined,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  SmartToyOutlined,
  OpenInFullOutlined,
  CloseFullscreenOutlined,
  RefreshOutlined,
  ImageOutlined,
  DoneAll,
  PsychologyOutlined,
  CheckCircleOutline,
  PublicOutlined,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import apiService, { BaseURL } from "../../service/Api";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { VenueData } from "../../pages/venues/types/venues.types";

// ==========================================
// Types & Interfaces
// ==========================================

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  media_url?: string;
}

export interface AgentActivityEvent {
  id: string;
  event_type:
  | "whatsapp"
  | "sql"
  | "search"
  | "image_search"
  | "web_scrape"
  | "task_start"
  | "task_complete"
  | "thought"
  | "error"
  | "info"
  | "init"
  | "ping"
  | "system";
  title: string;
  details?: string;
  status: "running" | "success" | "error" | "info" | "warning";
  timestamp: string;
  formatted_time?: string;
  extra?: Record<string, unknown>;
  // Present only on "init"/"ping" events, which double as a status snapshot.
  history?: AgentActivityEvent[];
  agent_status?: AgentStatusResponse;
  current_task?: string;
}

export interface ConversationItem {
  phone_number: string;
  name: string;
  role: string;
  is_crew: boolean;
  message_count: number;
  last_activity?: string | null;
  last_message?: string;
  last_role?: string;
  is_active: boolean;
}

export interface AgentStatusResponse {
  status: "idle" | "active" | "thinking" | "error";
  current_task?: string;
  active_booking_id?: number;
  active_venue_id?: number;
  active_phone?: string;
  task_started_at?: string;
  total_tasks_completed?: number;
  total_events_logged?: number;
}

// ==========================================
// Custom SVG Icons
// ==========================================

const BrandMIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.5 19V5.5L12 13.5L20.5 5.5V19"
      stroke={color}
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 2.2L19.7 3.8L21.3 4.5L19.7 5.2L19 6.8L18.3 5.2L16.7 4.5L18.3 3.8L19 2.2Z"
      fill={color}
    />
  </svg>
);

const WhatsAppIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = "#25D366",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.771-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.062-2.18-.553-1.637-.677-2.709-2.348-2.79-2.457-.082-.108-.667-.887-.667-1.691 0-.805.421-1.201.571-1.364.15-.164.33-.205.44-.205.111 0 .221.002.318.006.103.004.24-.039.375.285.14.336.478 1.168.52 1.253.042.085.07.185.014.298-.056.113-.085.183-.169.282-.085.099-.178.22-.254.296-.085.085-.174.177-.075.347.099.17.441.728.948 1.179.652.581 1.202.761 1.372.846.17.085.27.071.37-.044.1-.115.428-.499.542-.67.114-.17.228-.142.383-.085.155.056.985.464 1.155.549.17.085.283.127.325.198.042.071.042.41-.102.815zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.984-1.399C8.428 21.493 10.155 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.694 0-3.268-.535-4.57-1.442l-.328-.228-2.956.83.829-2.885-.23-.338C3.784 14.757 3.25 13.432 3.25 12c0-4.825 3.925-8.75 8.75-8.75 4.824 0 8.75 3.925 8.75 8.75 0 4.824-3.926 8.167-8.75 8.167z" />
  </svg>
);

// ==========================================
// Markdown & Formatted Message Text
// ==========================================

interface FormattedMessageTextProps {
  content: string;
  isUser?: boolean;
}

const FormattedMessageText: React.FC<FormattedMessageTextProps> = ({
  content,
  isUser,
}) => {
  if (!content) return null;

  const lines = content.split("\n");

  const parseInlineMarkdown = (text: string) => {
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
    const parts = text.split(regex);

    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        return (
          <Box
            key={idx}
            component="span"
            sx={{ fontWeight: 700, color: isUser ? "inherit" : "inherit" }}
          >
            {part.slice(2, -2)}
          </Box>
        );
      }
      if (
        (part.startsWith("*") && part.endsWith("*") && part.length > 2) ||
        (part.startsWith("_") && part.endsWith("_") && part.length > 2)
      ) {
        return (
          <Box key={idx} component="span" sx={{ fontStyle: "italic" }}>
            {part.slice(1, -1)}
          </Box>
        );
      }
      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return (
          <Box
            key={idx}
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.88em",
              bgcolor: isUser ? "rgba(0,0,0,0.08)" : "rgba(114, 78, 65, 0.08)",
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
            }}
          >
            {part.slice(1, -1)}
          </Box>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        return (
          <Box
            key={idx}
            component="a"
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isUser ? "#1d4ed8" : "#724e41",
              textDecoration: "underline",
              fontWeight: 600,
            }}
          >
            {linkMatch[1]}
          </Box>
        );
      }
      return part;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {lines.map((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <Box key={lineIdx} sx={{ height: 4 }} />;
        }

        if (trimmed.startsWith("### ")) {
          return (
            <Typography
              key={lineIdx}
              variant="subtitle2"
              sx={{ fontWeight: 700, mt: 0.5, mb: 0.2, fontSize: 13 }}
            >
              {parseInlineMarkdown(trimmed.slice(4))}
            </Typography>
          );
        }
        if (trimmed.startsWith("## ") || trimmed.startsWith("# ")) {
          return (
            <Typography
              key={lineIdx}
              variant="subtitle1"
              sx={{ fontWeight: 700, mt: 0.5, mb: 0.2, fontSize: 14 }}
            >
              {parseInlineMarkdown(trimmed.replace(/^#+\s*/, ""))}
            </Typography>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <Box
              key={lineIdx}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                pl: 0.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: "currentColor",
                  mt: 0.8,
                  flexShrink: 0,
                  opacity: 0.7,
                }}
              />
              <Typography variant="body2" sx={{ lineHeight: 1.45, fontSize: 13 }}>
                {parseInlineMarkdown(trimmed.slice(2))}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography
            key={lineIdx}
            variant="body2"
            sx={{ lineHeight: 1.45, fontSize: 13, wordBreak: "break-word" }}
          >
            {parseInlineMarkdown(line)}
          </Typography>
        );
      })}
    </Box>
  );
};

// ==========================================
// Manus-Style Grouped Agent Step Card (Single Row with Expandable History)
// ==========================================

interface ManusGroupedAgentStepCardProps {
  events: AgentActivityEvent[];
  isProcessing?: boolean;
  currentTaskTitle?: string;
}

const ManusGroupedAgentStepCard: React.FC<ManusGroupedAgentStepCardProps> = ({
  events,
  isProcessing = false,
  currentTaskTitle,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  // The latest event in this block
  const latestEvent = events[events.length - 1];
  const isRunning = isProcessing || latestEvent?.status === "running";
  const hasError = events.some((e) => e.status === "error");

  const titleText = isRunning
    ? currentTaskTitle || latestEvent?.title || "Nyasha executing actions..."
    : latestEvent?.title || "Agent task completed";

  const timeStr = useMemo(() => {
    const ev = latestEvent;
    if (ev?.formatted_time) return ev.formatted_time;
    if (!ev?.timestamp) return "";
    try {
      if (ev.timestamp.includes("T") || ev.timestamp.includes("-")) {
        const d = new Date(ev.timestamp);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      return ev.timestamp;
    } catch {
      return ev.timestamp;
    }
  }, [latestEvent]);

  if (!isProcessing && (!events || events.length === 0)) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        my: 0.85,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Step Header Row (Single Row with most recent action) */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: 0.4,
          px: 0.5,
          borderRadius: 1.5,
          transition: "background 0.15s ease",
          "&:hover": {
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "rgba(0,0,0,0.03)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          {/* Status Indicator Icon (Manus Check / Blue Glowing Dot) */}
          {isRunning ? (
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                bgcolor: "#38bdf8",
                boxShadow: "0 0 8px #38bdf8",
                animation: "pulse 1.4s infinite ease-in-out",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes pulse": {
                  "0%": { transform: "scale(0.85)", opacity: 0.7 },
                  "50%": { transform: "scale(1.15)", opacity: 1 },
                  "100%": { transform: "scale(0.85)", opacity: 0.7 },
                },
              }}
            />
          ) : hasError ? (
            <ErrorOutline
              sx={{ fontSize: 17, color: "#ef4444", flexShrink: 0 }}
            />
          ) : (
            <CheckCircleOutline
              sx={{
                fontSize: 17,
                color: theme.palette.mode === "dark" ? "#71717a" : "#94a3b8",
                flexShrink: 0,
              }}
            />
          )}

          {/* Step Title (Most Recent Action) */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: isRunning ? 700 : 600,
              fontSize: 12.5,
              color: isRunning
                ? theme.palette.mode === "dark"
                  ? "#38bdf8"
                  : "#0284c7"
                : theme.palette.mode === "dark"
                  ? "#e4e4e7"
                  : "#27272a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {titleText}
          </Typography>

          {/* Step Count Badge if multiple actions */}
          {events.length > 1 && (
            <Chip
              label={`${events.length} actions`}
              size="small"
              sx={{
                height: 16,
                fontSize: 9,
                fontWeight: 700,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                color: theme.palette.mode === "dark" ? "#cbd5e1" : "#64748b",
                px: 0.25,
              }}
            />
          )}

          {/* Chevron Indicator */}
          {expanded ? (
            <ExpandLess sx={{ fontSize: 15, color: "text.secondary" }} />
          ) : (
            <ExpandMore sx={{ fontSize: 15, color: "text.secondary" }} />
          )}
        </Box>

        {/* Timestamp on right */}
        {timeStr && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: theme.palette.mode === "dark" ? "#71717a" : "#a1a1aa",
              ml: 1,
              flexShrink: 0,
            }}
          >
            {timeStr}
          </Typography>
        )}
      </Box>

      {/* Expanded Manus Step Body (Timeline indented with left connecting rail) */}
      <Collapse in={expanded}>
        <Box
          sx={{
            ml: 1.1,
            pl: 2,
            my: 0.75,
            borderLeft: `2px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.08)"
            }`,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {events.map((ev, idx) => {
            const rawSql = ev.extra?.query;
            const evSql =
              (typeof rawSql === "string" && rawSql) ||
              (ev.event_type === "sql" && ev.details ? ev.details : null);

            let IconBadge = StorageOutlined;
            let subcardBadge = "Database Action";
            let color = "#3b82f6";

            if (ev.event_type === "search" || ev.event_type === "web_scrape") {
              IconBadge = PublicOutlined;
              subcardBadge = "Internet Search";
              color = "#8b5cf6";
            } else if (ev.event_type === "image_search") {
              IconBadge = ImageOutlined;
              subcardBadge = "Image Discovery";
              color = "#ec4899";
            } else if (ev.event_type === "thought") {
              IconBadge = PsychologyOutlined;
              subcardBadge = "Reasoning Step";
              color = "#f59e0b";
            } else if (ev.event_type === "whatsapp") {
              IconBadge = PhoneInTalkOutlined;
              subcardBadge = "WhatsApp Task";
              color = "#10b981";
            }

            return (
              <Paper
                key={`sub-act-${ev.id || idx}`}
                elevation={0}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "#f8fafc",
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.06)"
                  }`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: color,
                        color: "#ffffff",
                        borderRadius: 1.5,
                      }}
                    >
                      <IconBadge sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        fontSize={12}
                      >
                        {ev.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize={10}
                      >
                        {subcardBadge} •{" "}
                        {ev.formatted_time ||
                          (ev.timestamp ? ev.timestamp.slice(11, 19) : "")}
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    label={ev.status}
                    size="small"
                    sx={{
                      height: 15,
                      fontSize: 8,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      bgcolor:
                        ev.status === "success"
                          ? "rgba(16, 185, 129, 0.15)"
                          : ev.status === "running"
                            ? "rgba(245, 158, 11, 0.15)"
                            : "rgba(239, 68, 68, 0.15)",
                      color:
                        ev.status === "success"
                          ? "#10b981"
                          : ev.status === "running"
                            ? "#f59e0b"
                            : "#ef4444",
                    }}
                  />
                </Box>

                {/* Sub-text explanation */}
                {ev.details && ev.event_type !== "sql" && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 11,
                      lineHeight: 1.45,
                      color:
                        theme.palette.mode === "dark" ? "#a1a1aa" : "#4b5563",
                    }}
                  >
                    {ev.details}
                  </Typography>
                )}

                {/* SQL Code Block */}
                {evSql && (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      borderRadius: 1.5,
                      fontSize: 10.5,
                      fontFamily: "monospace",
                      bgcolor:
                        theme.palette.mode === "dark" ? "#090d16" : "#1e293b",
                      color: "#38bdf8",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      border: "1px solid rgba(56, 189, 248, 0.15)",
                    }}
                  >
                    <code>{evSql}</code>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

// ==========================================
// WhatsApp Message Bubble Component
// ==========================================

interface WhatsAppMessageBubbleProps {
  message: ChatMessage;
  contactName?: string;
  isCrew?: boolean;
}

const WhatsAppMessageBubble: React.FC<WhatsAppMessageBubbleProps> = ({
  message,
  contactName,
  isCrew,
}) => {
  const theme = useTheme();
  const isAi = message.role === "assistant";

  // Parse attached media url if present in content
  const cleanContent = useMemo(() => {
    let text = message.content || "";
    if (text.startsWith("[Media:") && text.includes("]")) {
      const splitIdx = text.indexOf("]");
      text = text.substring(splitIdx + 1).trim();
    }
    return text;
  }, [message.content]);

  const mediaUrl = useMemo(() => {
    if (message.media_url) return message.media_url;
    if (message.content && message.content.startsWith("[Media:")) {
      const match = message.content.match(/\[Media:\s*(https?:\/\/[^\]]+)\]/i);
      if (match && match[1]) return match[1];
    }
    return null;
  }, [message]);

  const timeString = useMemo(() => {
    if (!message.timestamp) return "";
    try {
      if (message.timestamp.includes("T") || message.timestamp.includes("-")) {
        const d = new Date(message.timestamp);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      return message.timestamp;
    } catch {
      return message.timestamp;
    }
  }, [message.timestamp]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isAi ? "flex-end" : "flex-start",
        width: "100%",
        my: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.75,
          flexDirection: isAi ? "row-reverse" : "row",
          maxWidth: { xs: "92%", sm: "85%" },
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: 26,
            height: 26,
            bgcolor: isAi
              ? "#724e41"
              : isCrew
                ? "#2563eb"
                : "#10b981",
            color: "#ffffff",
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {isAi ? (
            <BrandMIcon size={14} color="#ffffff" />
          ) : (
            (contactName || "U").charAt(0).toUpperCase()
          )}
        </Avatar>

        {/* Bubble */}
        <Paper
          elevation={1}
          sx={{
            px: 1.75,
            py: 1.25,
            borderRadius: 3,
            borderBottomRightRadius: isAi ? 0.5 : 3,
            borderBottomLeftRadius: isAi ? 3 : 0.5,
            bgcolor: isAi
              ? theme.palette.mode === "dark"
                ? "#3b261e"
                : "#f5ece6"
              : theme.palette.mode === "dark"
                ? "#1e293b"
                : "#ffffff",
            color: isAi
              ? theme.palette.mode === "dark"
                ? "#f1f5f9"
                : "#3b261e"
              : theme.palette.mode === "dark"
                ? "#f8fafc"
                : "#1e293b",
            border: isAi
              ? `1px solid ${theme.palette.mode === "dark" ? "#5c3b30" : "#decbc2"}`
              : `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0,0,0,0.3)"
                : "0 2px 8px rgba(0,0,0,0.04)",
            position: "relative",
          }}
        >
          {/* Sender label */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              fontSize: 10.5,
              color: isAi
                ? "#724e41"
                : isCrew
                  ? "#2563eb"
                  : "#10b981",
              display: "block",
              mb: 0.35,
            }}
          >
            {isAi ? "Nyasha AI (Muzukuru)" : contactName || "User"}
          </Typography>

          {/* Media preview if image attached */}
          {mediaUrl && (
            <Box
              component="img"
              src={mediaUrl}
              alt="Venue Media"
              sx={{
                width: "100%",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 2,
                mb: 1,
                display: "block",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          {/* Formatted Text */}
          <FormattedMessageText content={cleanContent} isUser={!isAi} />

          {/* Timestamp and ticks */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 9.5,
                color: "text.disabled",
                lineHeight: 1,
              }}
            >
              {timeString}
            </Typography>
            {isAi && (
              <DoneAll
                sx={{
                  fontSize: 13,
                  color: "#3b82f6", // WhatsApp blue double tick
                }}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

// ==========================================
// Main SiteVisitAiFloatingChat Component
// ==========================================

interface SiteVisitAiFloatingChatProps {
  venueData?: VenueData;
}

// Grouped Timeline unified item type
type TimelineItem =
  | {
      kind: "message";
      id: string;
      data: ChatMessage;
      timestampMs: number;
    }
  | {
      kind: "grouped_step";
      id: string;
      events: AgentActivityEvent[];
      timestampMs: number;
    };

export const SiteVisitAiFloatingChat: React.FC<SiteVisitAiFloatingChatProps> = ({
  venueData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Layout & Visibility State
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Active Tab: 'assistant' (web chat), 'activity' (system logs), or a phone number like '+263781646052'
  const [activeTab, setActiveTab] = useState<string>("assistant");

  // WhatsApp Conversations & Messages State
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [conversationMessages, setConversationMessages] = useState<
    Record<string, ChatMessage[]>
  >({});
  const [conversationActivities, setConversationActivities] = useState<
    Record<string, AgentActivityEvent[]>
  >({});
  const [activePhoneLoading, setActivePhoneLoading] = useState(false);

  // Web Assistant Chat State
  const [assistantMessages, setAssistantMessages] = useState<ChatMessage[]>([]);
  const [assistantInput, setAssistantInput] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);

  // WhatsApp Tab Input State (for sending/testing a WhatsApp reply)
  const [whatsappInput, setWhatsappInput] = useState("");
  const [whatsappSending, setWhatsappSending] = useState(false);

  // Global Agent Activity Tracker State
  const [agentEvents, setAgentEvents] = useState<AgentActivityEvent[]>([]);
  const [agentStatus, setAgentStatus] = useState<AgentStatusResponse | null>(null);
  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [activitySearchQuery, setActivitySearchQuery] = useState<string>("");

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const isAtBottomRef = useRef<boolean>(true);
  const [hasNewMessages, setHasNewMessages] = useState<boolean>(false);
  const prevTimelineLengthRef = useRef<number>(0);
  const user = useSelector((state: RootState) => state.auth.user) as User | null;

  const userName = useMemo(() => {
    if (user?.first_name?.trim()) {
      return user.first_name.trim().split(" ")[0];
    }
    if (user?.username?.trim()) {
      const cleanUser = user.username.trim().split("@")[0].split(".")[0];
      return cleanUser.charAt(0).toUpperCase() + cleanUser.slice(1);
    }
    return "there";
  }, [user]);

  // Scroll to bottom helper
  const scrollToBottom = useCallback((smooth = true) => {
    if (scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      if (smooth) {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: "smooth",
        });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } else if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    }
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    setHasNewMessages(false);
  }, []);

  // Handle scroll event on message container to detect if user scrolled up
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= 80;

    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);

    if (atBottom) {
      setHasNewMessages(false);
    }
  }, []);

  // ==========================================
  // API Fetchers
  // ==========================================

  // 1. Fetch Conversations List
  const fetchConversations = useCallback(async () => {
    try {
      const res = await apiService.get<ConversationItem[]>(
        "/api/venues/agent/conversations/"
      );
      if (Array.isArray(res.data)) {
        setConversations(res.data);
      }
    } catch (err) {
      console.warn("Could not fetch conversations list:", err);
    }
  }, []);

  // 2. Fetch Conversation Detail (messages & matching activities)
  const fetchConversationDetail = useCallback(
    async (phone: string, isBackground = false) => {
      if (!phone || phone === "assistant" || phone === "activity") return;
      try {
        if (!isBackground) {
          setActivePhoneLoading(true);
        }
        const res = await apiService.get<{
          phone_number: string;
          contact: { name: string; role: string; is_crew: boolean };
          messages: ChatMessage[];
          activities: AgentActivityEvent[];
        }>(`/api/venues/agent/conversations/${encodeURIComponent(phone)}/`);

        if (res.data?.messages) {
          setConversationMessages((prev) => ({
            ...prev,
            [phone]: res.data.messages,
          }));
        }
        if (res.data?.activities) {
          setConversationActivities((prev) => ({
            ...prev,
            [phone]: res.data.activities,
          }));
        }
      } catch (err) {
        console.warn(`Could not fetch details for conversation ${phone}:`, err);
      } finally {
        if (!isBackground) {
          setActivePhoneLoading(false);
        }
      }
    },
    []
  );

  // 3. Fetch Global Activity & Status
  const fetchActivityAndStatus = useCallback(async () => {
    try {
      const [actRes, statusRes] = await Promise.all([
        apiService.get<AgentActivityEvent[]>("/api/venues/agent/activity/"),
        apiService.get<AgentStatusResponse>("/api/venues/agent/status/"),
      ]);

      if (Array.isArray(actRes.data)) {
        setAgentEvents([...actRes.data].reverse());
      }
      if (statusRes.data) {
        setAgentStatus(statusRes.data);
      }
    } catch (err) {
      console.warn("Error fetching agent activity:", err);
    }
  }, []);

  // Effect when activeTab changes to a phone number
  useEffect(() => {
    if (activeTab && activeTab !== "assistant" && activeTab !== "activity") {
      fetchConversationDetail(activeTab, false);
    }
  }, [activeTab, fetchConversationDetail]);

  // ==========================================
  // Realtime SSE Stream & Polling Setup
  // ==========================================

  useEffect(() => {
    if (!isOpen) return;

    fetchConversations();
    fetchActivityAndStatus();

    const pollInterval = setInterval(() => {
      fetchConversations();
      fetchActivityAndStatus();
      if (activeTab && activeTab !== "assistant" && activeTab !== "activity") {
        fetchConversationDetail(activeTab, true);
      }
    }, 2500);

    let eventSource: EventSource | null = null;
    try {
      const streamPath = "/api/venues/agent/activity/stream/";
      const streamUrl = BaseURL.startsWith("http")
        ? `${BaseURL}${streamPath}`
        : streamPath;
      eventSource = new EventSource(streamUrl);

      eventSource.onmessage = (event) => {
        try {
          const data: AgentActivityEvent = JSON.parse(event.data);
          if (data.event_type === "init") {
            if (Array.isArray(data.history)) {
              setAgentEvents([...data.history].reverse());
            }
            if (data.agent_status) {
              setAgentStatus(data.agent_status);
            }
            fetchConversations();
          } else if (data.event_type === "ping") {
            if (data.agent_status) {
              setAgentStatus(data.agent_status);
            }
          } else {
            setAgentEvents((prev) => {
              if (prev.some((e) => e.id === data.id)) return prev;
              return [data, ...prev];
            });

            const rawPhone = data.extra?.phone || data.extra?.sender;
            const evPhone =
              typeof rawPhone === "string" || typeof rawPhone === "number"
                ? String(rawPhone)
                : "";
            if (evPhone) {
              setConversationActivities((prev) => ({
                ...prev,
                [evPhone]: [data, ...(prev[evPhone] || [])],
              }));
              fetchConversations();
            }
          }
        } catch (e) {
          console.error("Failed to parse SSE event:", e);
        }
      };

      eventSource.onerror = (err) => {
        console.warn("SSE connection notice:", err);
      };
    } catch (e) {
      console.warn("Could not initiate EventSource:", e);
    }

    return () => {
      clearInterval(pollInterval);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [
    isOpen,
    activeTab,
    fetchConversations,
    fetchActivityAndStatus,
    fetchConversationDetail,
  ]);

  // ==========================================
  // Handlers: Interactive Web Chat
  // ==========================================

  const handleSendAssistantMessage = async (textToSend?: string) => {
    const queryText = (textToSend || assistantInput).trim();
    if (!queryText || assistantLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: queryText,
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...assistantMessages, userMsg];
    setAssistantMessages(updatedMessages);
    if (!textToSend) setAssistantInput("");
    setAssistantLoading(true);
    setTimeout(() => scrollToBottom(true), 40);

    setAgentStatus({
      status: "active",
      current_task: `Web Chat: "${queryText.slice(0, 45)}..."`,
    });

    const tempEvent: AgentActivityEvent = {
      id: `temp-${Date.now()}`,
      event_type: "task_start",
      title: "Interactive Web Query",
      details: `User Prompt: ${queryText}`,
      status: "running",
      timestamp: new Date().toISOString(),
      formatted_time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setAgentEvents((prev) => [tempEvent, ...prev]);

    try {
      let fullPrompt = queryText;
      if (venueData && assistantMessages.length <= 2) {
        fullPrompt = `[Context: Viewing Venue ID ${venueData.id} - ${venueData.name
          }, City: ${venueData.city || "N/A"}, Power: ${venueData.has_power ? "Yes" : "No"
          }, ISP: ${venueData.internet_service_provider || "N/A"}]\n${queryText}`;
      }

      const payloadMessages = updatedMessages.map((m) => ({
        role: m.role,
        content: m.id === userMsg.id ? fullPrompt : m.content,
      }));

      const res = await apiService.post<{
        response?: string;
        messages?: Array<{ role: string; content: string }>;
      }>("/api/venues/agent/chat/", {
        messages: payloadMessages,
      });

      const rawResponse =
        res.data?.response ||
        (res.data?.messages && res.data.messages.length > 0
          ? res.data.messages[res.data.messages.length - 1].content
          : "I received your request successfully.");

      const aiMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: rawResponse,
        timestamp: new Date().toISOString(),
      };

      setAssistantMessages((prev) => [...prev, aiMsg]);
    } catch (err: unknown) {
      console.error("Error communicating with Site Visit AI agent:", err);
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: getErrorMessage(
          err,
          "Sorry, I encountered an issue connecting to Nyasha AI server. Please verify your connection.",
        ),
        timestamp: new Date().toISOString(),
      };
      setAssistantMessages((prev) => [...prev, errorMsg]);
    } finally {
      setAssistantLoading(false);
      fetchActivityAndStatus();
    }
  };

  // ==========================================
  // Handlers: WhatsApp Conversation Test Message
  // ==========================================

  const handleSendWhatsAppMessage = async (phone: string) => {
    const text = whatsappInput.trim();
    if (!text || whatsappSending) return;

    setWhatsappSending(true);
    setWhatsappInput("");

    const tempUserMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };

    setConversationMessages((prev) => ({
      ...prev,
      [phone]: [...(prev[phone] || []), tempUserMsg],
    }));
    setTimeout(() => scrollToBottom(true), 40);

    try {
      await apiService.post(
        `/api/venues/agent/conversations/${encodeURIComponent(phone)}/`,
        { message: text }
      );
      setTimeout(() => {
        fetchConversationDetail(phone, true);
        fetchConversations();
        fetchActivityAndStatus();
      }, 800);
    } catch (err) {
      console.error("Failed to send WhatsApp message simulation:", err);
    } finally {
      setWhatsappSending(false);
    }
  };

  // ==========================================
  // Computed & Filtered Items
  // ==========================================

  const currentConversation = useMemo(() => {
    return conversations.find((c) => c.phone_number === activeTab);
  }, [conversations, activeTab]);

  // Extract all relevant tool activities for active tab
  const activeTabActivities = useMemo<AgentActivityEvent[]>(() => {
    if (activeTab === "activity") return [];

    if (activeTab === "assistant") {
      return agentEvents.filter(
        (e) =>
          e.event_type === "sql" ||
          e.event_type === "search" ||
          e.event_type === "image_search" ||
          e.event_type === "thought" ||
          e.event_type === "task_start" ||
          e.event_type === "task_complete"
      );
    }

    const directActs = conversationActivities[activeTab] || [];
    const cleanPhone = activeTab.replace(/\D/g, "");
    const suffix = cleanPhone.slice(-9);

    const matchingEvents = agentEvents.filter((e) => {
      const ePhone = String(e.extra?.phone || e.extra?.sender || "").replace(
        /\D/g,
        ""
      );
      if (suffix && ePhone && (ePhone.endsWith(suffix) || suffix.endsWith(ePhone))) {
        return true;
      }
      return false;
    });

    const map = new Map<string, AgentActivityEvent>();
    directActs.forEach((a) => map.set(a.id, a));
    matchingEvents.forEach((a) => map.set(a.id, a));

    const merged = Array.from(map.values());
    return merged.filter(
      (a) =>
        a.event_type === "sql" ||
        a.event_type === "search" ||
        a.event_type === "image_search" ||
        a.event_type === "web_scrape" ||
        a.event_type === "thought" ||
        a.event_type === "whatsapp" ||
        a.event_type === "error"
    );
  }, [activeTab, conversationActivities, agentEvents]);

  // ==========================================
  // Grouped Chronological Timeline Stream (Manus Style: 1 Row per Action Turn)
  // ==========================================

  const conversationTimeline = useMemo<TimelineItem[]>(() => {
    if (!activeTab || activeTab === "activity") return [];

    const msgs =
      activeTab === "assistant"
        ? assistantMessages
        : conversationMessages[activeTab] || [];
    const activities = activeTabActivities;

    // Filter relevant tool activities
    const toolActivities = activities.filter(
      (act) =>
        act.event_type === "sql" ||
        act.event_type === "search" ||
        act.event_type === "image_search" ||
        act.event_type === "web_scrape" ||
        act.event_type === "thought" ||
        act.event_type === "whatsapp" ||
        act.event_type === "error"
    );

    // Collect all elements with unified timestamps
    interface RawItem {
      kind: "message" | "activity";
      msgData?: ChatMessage;
      actData?: AgentActivityEvent;
      timestampMs: number;
      id: string;
    }

    const rawList: RawItem[] = [];

    msgs.forEach((m, idx) => {
      let tMs = 0;
      if (m.timestamp) {
        const parsed = Date.parse(m.timestamp);
        tMs = isNaN(parsed) ? idx * 1000 : parsed;
      } else {
        tMs = idx * 1000;
      }
      rawList.push({
        kind: "message",
        msgData: m,
        timestampMs: tMs,
        id: `msg-${m.id || idx}`,
      });
    });

    toolActivities.forEach((act, idx) => {
      let tMs = 0;
      if (act.timestamp) {
        const parsed = Date.parse(act.timestamp);
        tMs = isNaN(parsed) ? 0 : parsed;
      }
      rawList.push({
        kind: "activity",
        actData: act,
        timestampMs: tMs,
        id: `act-${act.id || idx}`,
      });
    });

    // Sort chronologically
    rawList.sort((a, b) => a.timestampMs - b.timestampMs);

    // Group consecutive activities into a SINGLE grouped_step item
    const items: TimelineItem[] = [];
    let currentActivityGroup: AgentActivityEvent[] = [];
    let groupTimestampMs = 0;

    const flushActivityGroup = () => {
      if (currentActivityGroup.length > 0) {
        items.push({
          kind: "grouped_step",
          id: `grouped-step-${currentActivityGroup[0].id}-${items.length}`,
          events: [...currentActivityGroup],
          timestampMs: groupTimestampMs,
        });
        currentActivityGroup = [];
      }
    };

    rawList.forEach((item) => {
      if (item.kind === "message" && item.msgData) {
        flushActivityGroup();
        items.push({
          kind: "message",
          id: item.id,
          data: item.msgData,
          timestampMs: item.timestampMs,
        });
      } else if (item.kind === "activity" && item.actData) {
        currentActivityGroup.push(item.actData);
        groupTimestampMs = item.timestampMs;
      }
    });

    // Flush any trailing activities
    flushActivityGroup();

    return items;
  }, [activeTab, assistantMessages, conversationMessages, activeTabActivities]);

  // Auto-scroll on initial load or tab switch
  useEffect(() => {
    if (isOpen) {
      isAtBottomRef.current = true;
      setIsAtBottom(true);
      setHasNewMessages(false);
      prevTimelineLengthRef.current = conversationTimeline.length;

      const timer = setTimeout(() => {
        scrollToBottom(false);
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [conversationTimeline.length, isOpen, activeTab, scrollToBottom]);

  // When timeline items update (new messages or agent activities)
  useEffect(() => {
    if (!isOpen) return;

    const currentLength = conversationTimeline.length;
    const prevLength = prevTimelineLengthRef.current;
    const hasAddedItems = currentLength > prevLength;
    prevTimelineLengthRef.current = currentLength;

    if (hasAddedItems) {
      if (isAtBottomRef.current) {
        scrollToBottom(true);
      } else {
        setHasNewMessages(true);
      }
    }
  }, [conversationTimeline.length, isOpen, scrollToBottom]);

  // Handle window expansion resize
  useEffect(() => {
    if (isOpen && isAtBottomRef.current) {
      const timer = setTimeout(() => {
        scrollToBottom(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, isOpen, scrollToBottom]);

  // Activity counts
  const whatsappCount = useMemo(
    () => agentEvents.filter((e) => e.event_type === "whatsapp").length,
    [agentEvents]
  );
  const sqlCount = useMemo(
    () => agentEvents.filter((e) => e.event_type === "sql").length,
    [agentEvents]
  );
  const searchCount = useMemo(
    () =>
      agentEvents.filter(
        (e) =>
          e.event_type === "search" ||
          e.event_type === "image_search" ||
          e.event_type === "web_scrape"
      ).length,
    [agentEvents]
  );
  const tasksCount = useMemo(
    () =>
      agentEvents.filter(
        (e) =>
          e.event_type === "task_start" ||
          e.event_type === "task_complete" ||
          e.event_type === "thought"
      ).length,
    [agentEvents]
  );

  const filteredTelemetryEvents = useMemo(() => {
    return agentEvents.filter((event) => {
      if (activityFilter === "whatsapp" && event.event_type !== "whatsapp")
        return false;
      if (activityFilter === "sql" && event.event_type !== "sql") return false;
      if (
        activityFilter === "search" &&
        !["search", "image_search", "web_scrape"].includes(event.event_type)
      )
        return false;
      if (
        activityFilter === "tasks" &&
        !["task_start", "task_complete", "thought"].includes(event.event_type)
      )
        return false;

      if (activitySearchQuery.trim()) {
        const q = activitySearchQuery.toLowerCase();
        const titleMatch = event.title?.toLowerCase().includes(q);
        const detailsMatch = event.details?.toLowerCase().includes(q);
        return titleMatch || detailsMatch;
      }
      return true;
    });
  }, [agentEvents, activityFilter, activitySearchQuery]);

  const quickPrompts = [
    {
      shortLabel: "Site Visits",
      fullLabel: `What site visits are scheduled for ${venueData?.name || "this venue"
        }?`,
      icon: <EventNoteOutlined fontSize="small" />,
      query: `What site visits are scheduled for ${venueData?.name || "this venue"
        }?`,
    },
    {
      shortLabel: "Power & Wifi",
      fullLabel: `Check power backup and Wi-Fi setup`,
      icon: <ElectricBoltOutlined fontSize="small" />,
      query: `Check power backup and internet details for ${venueData?.name || "this venue"
        }.`,
    },
    {
      shortLabel: "Venue Health",
      fullLabel: `Analyze data completeness and venue health`,
      icon: <AnalyticsOutlined fontSize="small" />,
      query: `Analyze data completeness and readiness for ${venueData?.name || "this venue"
        }.`,
    },
  ];

  // Active status check for active tab
  const isTabActiveProcessing = useMemo<boolean>(() => {
    if (activeTab === "assistant") {
      return Boolean(assistantLoading);
    }
    if (activeTab !== "activity") {
      return Boolean(
        agentStatus?.status === "active" &&
        (agentStatus?.active_phone === activeTab ||
          (agentStatus?.current_task &&
            agentStatus.current_task.includes(activeTab)))
      );
    }
    return Boolean(agentStatus?.status === "active");
  }, [activeTab, assistantLoading, agentStatus]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: isOpen ? 20 : 100,
        right: 24,
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      {/* Floating Chat Modal */}
      <Collapse in={isOpen} unmountOnExit sx={{ mb: 0 }}>
        <Paper
          elevation={16}
          sx={{
            width: isExpanded
              ? { xs: "calc(100vw - 32px)", sm: 820 }
              : { xs: "calc(100vw - 32px)", sm: 460 },
            height: isExpanded ? 720 : 600,
            maxHeight: "92vh",
            borderRadius: 3.5,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: `1px solid ${theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(0, 0, 0, 0.08)"
              }`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.75)"
                : "0 25px 50px -12px rgba(114, 78, 65, 0.25)",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s ease",
          }}
        >
          {/* Main Top Header */}
          <Box
            sx={{
              p: 1.5,
              background: "linear-gradient(135deg, #724e41 0%, #8c5a45 100%)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#10b981",
                    color: "#10b981",
                    boxShadow: "0 0 0 2px white",
                    "&::after": {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      animation: "ripple 1.4s infinite ease-in-out",
                      border: "1px solid currentColor",
                      content: '""',
                    },
                  },
                  "@keyframes ripple": {
                    "0%": { transform: "scale(.8)", opacity: 1 },
                    "100%": { transform: "scale(2.4)", opacity: 0 },
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    width: 36,
                    height: 36,
                  }}
                >
                  <BrandMIcon size={18} color="#ffffff" />
                </Avatar>
              </Badge>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ lineHeight: 1.1, color: "#ffffff", fontSize: 14.5 }}
                  >
                    Nyasha AI
                  </Typography>
                  <Chip
                    label="Live Hub"
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 800,
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      color: "#ffffff",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.82)", fontSize: 11 }}
                >
                  Site Visit & Multi-Agent Operations
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Tooltip title="Refresh conversations & status">
                <IconButton
                  size="small"
                  onClick={() => {
                    fetchConversations();
                    fetchActivityAndStatus();
                    if (
                      activeTab !== "assistant" &&
                      activeTab !== "activity"
                    ) {
                      fetchConversationDetail(activeTab);
                    }
                  }}
                  sx={{ color: "rgba(255, 255, 255, 0.85)" }}
                >
                  <RefreshOutlined fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={isExpanded ? "Restore compact size" : "Expand window"}>
                <IconButton
                  size="small"
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{ color: "rgba(255, 255, 255, 0.85)" }}
                >
                  {isExpanded ? (
                    <CloseFullscreenOutlined fontSize="small" />
                  ) : (
                    <OpenInFullOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Minimize">
                <IconButton
                  size="small"
                  onClick={() => setIsOpen(false)}
                  sx={{ color: "rgba(255, 255, 255, 0.85)" }}
                >
                  <Minimize fontSize="small" />
                </IconButton>
              </Tooltip>

              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: "rgba(255, 255, 255, 0.85)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Dynamic Multi-Channel Conversation Tabs */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.75,
              flexShrink: 0,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.3)"
                  : "rgba(114, 78, 65, 0.06)",
              borderBottom: `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.06)"
                }`,
              overflowX: "auto",
              "::-webkit-scrollbar": { height: 4 },
              "::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(114, 78, 65, 0.2)",
                borderRadius: 2,
              },
            }}
          >
            {/* Tab: Interactive Assistant */}
            <Paper
              elevation={0}
              onClick={() => setActiveTab("assistant")}
              sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0,
                transition: "all 0.2s ease",
                bgcolor:
                  activeTab === "assistant"
                    ? "#724e41"
                    : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "#ffffff",
                color: activeTab === "assistant" ? "#ffffff" : "inherit",
                border: `1px solid ${activeTab === "assistant"
                  ? "#724e41"
                  : theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow:
                  activeTab === "assistant"
                    ? "0 2px 6px rgba(114, 78, 65, 0.3)"
                    : "none",
              }}
            >
              <ChatBubbleOutline
                sx={{
                  fontSize: 14,
                  color: activeTab === "assistant" ? "#ffffff" : "#724e41",
                }}
              />
              <Typography variant="body2" fontWeight={700} fontSize={12}>
                Assistant (Web)
              </Typography>
            </Paper>

            {/* Dynamic Tabs: WhatsApp Phone Number Conversations */}
            {conversations.map((conv) => {
              const isSelected = activeTab === conv.phone_number;
              const isLive =
                conv.is_active ||
                agentStatus?.active_phone === conv.phone_number;

              return (
                <Paper
                  key={conv.phone_number}
                  elevation={0}
                  onClick={() => setActiveTab(conv.phone_number)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 2,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    bgcolor: isSelected
                      ? "#10b981"
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "#ffffff",
                    color: isSelected ? "#ffffff" : "inherit",
                    border: `1px solid ${isSelected
                      ? "#10b981"
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                      }`,
                    boxShadow: isSelected
                      ? "0 2px 6px rgba(16, 185, 129, 0.3)"
                      : "none",
                  }}
                >
                  <WhatsAppIcon
                    size={14}
                    color={isSelected ? "#ffffff" : "#10b981"}
                  />
                  <Typography variant="body2" fontWeight={700} fontSize={12}>
                    {conv.name || conv.phone_number}
                  </Typography>

                  {isLive && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: isSelected ? "#ffffff" : "#10b981",
                        animation: "pulse 1.2s infinite ease-in-out",
                      }}
                    />
                  )}

                  {conv.is_crew && (
                    <Chip
                      label="Crew"
                      size="small"
                      sx={{
                        height: 14,
                        fontSize: 8.5,
                        fontWeight: 800,
                        bgcolor: isSelected
                          ? "rgba(255,255,255,0.25)"
                          : "rgba(37, 99, 235, 0.12)",
                        color: isSelected ? "#ffffff" : "#2563eb",
                        px: 0.25,
                      }}
                    />
                  )}
                </Paper>
              );
            })}

            {/* Tab: System Telemetry / Logs */}
            <Paper
              elevation={0}
              onClick={() => setActiveTab("activity")}
              sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0,
                transition: "all 0.2s ease",
                bgcolor:
                  activeTab === "activity"
                    ? "#334155"
                    : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "#ffffff",
                color: activeTab === "activity" ? "#ffffff" : "inherit",
                border: `1px solid ${activeTab === "activity"
                  ? "#334155"
                  : theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow:
                  activeTab === "activity"
                    ? "0 2px 6px rgba(51, 65, 85, 0.3)"
                    : "none",
              }}
            >
              <SensorsOutlined
                sx={{
                  fontSize: 14,
                  color: activeTab === "activity" ? "#ffffff" : "#64748b",
                }}
              />
              <Typography variant="body2" fontWeight={700} fontSize={12}>
                Telemetry & Logs
              </Typography>
              {agentEvents.length > 0 && (
                <Chip
                  label={agentEvents.length}
                  size="small"
                  sx={{
                    height: 14,
                    fontSize: 9,
                    fontWeight: 800,
                    bgcolor:
                      activeTab === "activity"
                        ? "rgba(255,255,255,0.25)"
                        : "rgba(0,0,0,0.08)",
                    color: activeTab === "activity" ? "#ffffff" : "inherit",
                  }}
                />
              )}
            </Paper>
          </Box>

          {/* ========================================== */}
          {/* TAB CONTENT 1: Interactive Assistant (Web) */}
          {/* ========================================== */}
          {activeTab === "assistant" && (
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? colors.primary[500]
                    : "#ffffff",
              }}
            >
              {/* Context bar */}
              {venueData && (
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(114, 78, 65, 0.05)",
                    borderBottom: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)"
                      }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#5c3b30",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "280px",
                    }}
                  >
                    Context: {venueData.name} ({venueData.city || "Venue View"})
                  </Typography>
                  <Chip
                    label="AI Active"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 9.5,
                      fontWeight: 800,
                      borderColor: "#724e41",
                      color: "#724e41",
                    }}
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Chat Message Stream with Grouped Action Timeline */}
              <Box
                sx={{
                  position: "relative",
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    p: 2,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                  }}
                >
                  {conversationTimeline.length === 0 ? (
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ mb: 2.5, width: "100%", textAlign: "left" }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            background:
                              theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)"
                                : "linear-gradient(135deg, #724e41 0%, #2563eb 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            mb: 0.5,
                          }}
                        >
                          Hie {userName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          Ask questions about venue specs, power, Wi-Fi, or monitor
                          site visit tasks.
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {quickPrompts.map((item, idx) => (
                          <Paper
                            key={idx}
                            elevation={0}
                            onClick={() => handleSendAssistantMessage(item.query)}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              transition: "all 0.2s ease",
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.04)"
                                  : "#ffffff",
                              border: `1px solid ${theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(0, 0, 0, 0.07)"
                                }`,
                              "&:hover": {
                                borderColor: "#724e41",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "rgba(114, 78, 65, 0.1)",
                                color: "#724e41",
                              }}
                            >
                              {item.icon}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: 12.5,
                                flexGrow: 1,
                              }}
                            >
                              {item.fullLabel}
                            </Typography>
                            <East
                              fontSize="small"
                              sx={{ color: "text.disabled", fontSize: 16 }}
                            />
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    conversationTimeline.map((item) => {
                      if (item.kind === "message") {
                        return (
                          <WhatsAppMessageBubble
                            key={item.id}
                            message={item.data}
                            contactName={user?.first_name || "Staff"}
                          />
                        );
                      } else {
                        // 1 Single Row per Action Block
                        return (
                          <ManusGroupedAgentStepCard
                            key={item.id}
                            events={item.events}
                          />
                        );
                      }
                    })
                  )}

                  {/* Live running step when Nyasha is currently processing and no grouped_step is active */}
                  {assistantLoading &&
                    conversationTimeline[conversationTimeline.length - 1]
                      ?.kind !== "grouped_step" && (
                      <ManusGroupedAgentStepCard
                        events={activeTabActivities}
                        isProcessing={true}
                        currentTaskTitle={agentStatus?.current_task}
                      />
                    )}
                  <div ref={chatEndRef} />
                </Box>

                {/* Floating Scroll to Bottom Jump Button */}
                <Zoom in={!isAtBottom}>
                  <Paper
                    elevation={4}
                    onClick={() => scrollToBottom(true)}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 16,
                      zIndex: 20,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      py: hasNewMessages ? 0.75 : 0.6,
                      px: hasNewMessages ? 1.5 : 1,
                      borderRadius: 6,
                      bgcolor: "#724e41",
                      color: "#ffffff",
                      boxShadow: "0 4px 16px rgba(114, 78, 65, 0.45)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-2px) scale(1.04)",
                        bgcolor: "#5c3b30",
                        boxShadow: "0 6px 22px rgba(114, 78, 65, 0.6)",
                      },
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 18, color: "#ffffff" }} />
                    {hasNewMessages && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: 0.2,
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        New messages
                      </Typography>
                    )}
                  </Paper>
                </Zoom>
              </Box>

              <Divider />

              {/* Input bar */}
              <Box
                sx={{
                  p: 1.25,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? colors.primary[400]
                      : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ask Nyasha about this venue or task..."
                  value={assistantInput}
                  onChange={(e) => setAssistantInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendAssistantMessage();
                    }
                  }}
                  disabled={assistantLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: 13,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? colors.primary[500]
                          : "#fcf8f5",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleSendAssistantMessage()}
                          disabled={!assistantInput.trim() || assistantLoading}
                          sx={{
                            bgcolor: assistantInput.trim()
                              ? "#724e41"
                              : "transparent",
                            color: assistantInput.trim()
                              ? "#ffffff"
                              : "action.disabled",
                            "&:hover": { bgcolor: "#5c3b30" },
                            width: 30,
                            height: 30,
                          }}
                        >
                          <Send fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          )}

          {/* ========================================== */}
          {/* TAB CONTENT 2: Real WhatsApp Phone Tab    */}
          {/* ========================================== */}
          {activeTab !== "assistant" && activeTab !== "activity" && (
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? colors.primary[500]
                    : "#ffffff",
              }}
            >
              {/* WhatsApp Contact Header Card */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(0,0,0,0.3)"
                      : "#ffffff",
                  borderBottom: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)"
                    }`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: currentConversation?.is_crew
                        ? "#2563eb"
                        : "#10b981",
                      color: "#ffffff",
                      fontWeight: 700,
                    }}
                  >
                    {(currentConversation?.name || "C").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        sx={{ fontSize: 13.5 }}
                      >
                        {currentConversation?.name || activeTab}
                      </Typography>
                      <WhatsAppIcon size={14} color="#10b981" />
                      <Chip
                        label={
                          currentConversation?.is_crew
                            ? "Assigned Crew"
                            : "Venue Coordinator"
                        }
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          fontWeight: 800,
                          bgcolor: currentConversation?.is_crew
                            ? "rgba(37, 99, 235, 0.12)"
                            : "rgba(16, 185, 129, 0.12)",
                          color: currentConversation?.is_crew
                            ? "#2563eb"
                            : "#10b981",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: 11 }}
                    >
                      {activeTab} •{" "}
                      {isTabActiveProcessing ? (
                        <Box
                          component="span"
                          sx={{ color: "#10b981", fontWeight: 700 }}
                        >
                          ⚡ Nyasha executing actions...
                        </Box>
                      ) : (
                        "WhatsApp Channel Live"
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title="Refresh conversation messages">
                    <IconButton
                      size="small"
                      onClick={() => fetchConversationDetail(activeTab)}
                    >
                      <RefreshOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* WhatsApp Messages & Grouped Action Timeline Stream */}
              <Box
                sx={{
                  position: "relative",
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    p: 2,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? colors.primary[500]
                        : "#fdfbf9",
                  }}
                >
                  {activePhoneLoading && conversationTimeline.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 6,
                      }}
                    >
                      <CircularProgress size={24} sx={{ color: "#10b981", mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Loading conversation thread...
                      </Typography>
                    </Box>
                  ) : conversationTimeline.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 6,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <WhatsAppIcon size={40} color="#10b981" />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1.5 }}>
                        No messages on this channel yet
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.5, maxWidth: 280 }}>
                        When Nyasha coordinates site visits or receives incoming
                        WhatsApp messages from {currentConversation?.name || activeTab},
                        conversations and actions will appear here in chronological order.
                      </Typography>
                    </Box>
                  ) : (
                    conversationTimeline.map((item) => {
                      if (item.kind === "message") {
                        return (
                          <WhatsAppMessageBubble
                            key={item.id}
                            message={item.data}
                            contactName={currentConversation?.name || activeTab}
                            isCrew={currentConversation?.is_crew}
                          />
                        );
                      } else {
                        // 1 Single Row per Action Block
                        return (
                          <ManusGroupedAgentStepCard
                            key={item.id}
                            events={item.events}
                          />
                        );
                      }
                    })
                  )}

                  {/* Live running step when Nyasha is currently processing and no grouped_step is active */}
                  {isTabActiveProcessing &&
                    conversationTimeline[conversationTimeline.length - 1]
                      ?.kind !== "grouped_step" && (
                      <ManusGroupedAgentStepCard
                        events={activeTabActivities}
                        isProcessing={true}
                        currentTaskTitle={agentStatus?.current_task}
                      />
                    )}
                  <div ref={chatEndRef} />
                </Box>

                {/* Floating Scroll to Bottom Jump Button */}
                <Zoom in={!isAtBottom}>
                  <Paper
                    elevation={4}
                    onClick={() => scrollToBottom(true)}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 16,
                      zIndex: 20,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      py: hasNewMessages ? 0.75 : 0.6,
                      px: hasNewMessages ? 1.5 : 1,
                      borderRadius: 6,
                      bgcolor: "#10b981",
                      color: "#ffffff",
                      boxShadow: "0 4px 16px rgba(16, 185, 129, 0.45)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-2px) scale(1.04)",
                        bgcolor: "#059669",
                        boxShadow: "0 6px 22px rgba(16, 185, 129, 0.6)",
                      },
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 18, color: "#ffffff" }} />
                    {hasNewMessages && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: 0.2,
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        New messages
                      </Typography>
                    )}
                  </Paper>
                </Zoom>
              </Box>

              <Divider />

              {/* WhatsApp Message Input Bar */}
              <Box
                sx={{
                  p: 1.25,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? colors.primary[400]
                      : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Send WhatsApp simulation reply to ${currentConversation?.name || "contact"}...`}
                  value={whatsappInput}
                  onChange={(e) => setWhatsappInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendWhatsAppMessage(activeTab);
                    }
                  }}
                  disabled={whatsappSending}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      fontSize: 13,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? colors.primary[500]
                          : "#f4fdf8",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleSendWhatsAppMessage(activeTab)}
                          disabled={!whatsappInput.trim() || whatsappSending}
                          sx={{
                            bgcolor: whatsappInput.trim()
                              ? "#10b981"
                              : "transparent",
                            color: whatsappInput.trim()
                              ? "#ffffff"
                              : "action.disabled",
                            "&:hover": { bgcolor: "#059669" },
                            width: 30,
                            height: 30,
                          }}
                        >
                          <Send fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          )}

          {/* ========================================== */}
          {/* TAB CONTENT 3: System Telemetry & Logs    */}
          {/* ========================================== */}
          {activeTab === "activity" && (
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? colors.primary[500]
                    : "#f8fafc",
              }}
            >
              {/* Metric Counter Cards */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "#ffffff",
                  borderBottom: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)"
                    }`,
                  flexShrink: 0,
                }}
              >
                {/* Live Status Bar */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    mb: 1.25,
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(114, 78, 65, 0.2)"
                        : "rgba(114, 78, 65, 0.05)",
                    border: `1px solid ${theme.palette.mode === "dark"
                      ? "rgba(114, 78, 65, 0.4)"
                      : "rgba(114, 78, 65, 0.15)"
                      }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor:
                          agentStatus?.status === "active" ||
                            agentStatus?.status === "thinking"
                            ? "#10b981"
                            : "#94a3b8",
                        boxShadow:
                          agentStatus?.status === "active" ||
                            agentStatus?.status === "thinking"
                            ? "0 0 8px #10b981"
                            : "none",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color:
                          theme.palette.mode === "dark" ? "#f1f5f9" : "#334155",
                      }}
                    >
                      Status:{" "}
                      {agentStatus?.status === "active"
                        ? "Active Executing Task"
                        : agentStatus?.status === "thinking"
                          ? "Nyasha AI Thinking..."
                          : "Idle (Ready)"}
                    </Typography>
                  </Box>

                  <Chip
                    label="Live Stream"
                    size="small"
                    icon={<SensorsOutlined sx={{ fontSize: "12px !important" }} />}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: "#10b981",
                      color: "#ffffff",
                      "& .MuiChip-icon": { color: "#ffffff" },
                    }}
                  />
                </Paper>

                {/* 4 Metric Cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <StorageOutlined
                      sx={{ fontSize: 16, color: "#3b82f6", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {sqlCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      SQL Queries
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <SearchOutlined
                      sx={{ fontSize: 16, color: "#8b5cf6", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {searchCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      Searches
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <PhoneInTalkOutlined
                      sx={{ fontSize: 16, color: "#10b981", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {whatsappCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      WhatsApp
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f1f5f9",
                    }}
                  >
                    <SmartToyOutlined
                      sx={{ fontSize: 16, color: "#f59e0b", mb: 0.2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={800} fontSize={13}>
                      {tasksCount}
                    </Typography>
                    <Typography variant="caption" fontSize={9} color="text.secondary">
                      Tasks
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              {/* Filter Pills & Search */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(0,0,0,0.2)"
                      : "#ffffff",
                  borderBottom: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.06)"
                    }`,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    overflowX: "auto",
                    flexGrow: 1,
                    "::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {["all", "sql", "search", "whatsapp", "tasks"].map((f) => (
                    <Chip
                      key={f}
                      label={f.toUpperCase()}
                      size="small"
                      onClick={() => setActivityFilter(f)}
                      sx={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 700,
                        height: 22,
                        bgcolor:
                          activityFilter === f ? "#724e41" : "transparent",
                        color: activityFilter === f ? "#ffffff" : "inherit",
                      }}
                    />
                  ))}
                </Box>

                <TextField
                  size="small"
                  placeholder="Filter logs..."
                  value={activitySearchQuery}
                  onChange={(e) => setActivitySearchQuery(e.target.value)}
                  sx={{
                    width: 120,
                    "& .MuiOutlinedInput-root": {
                      height: 24,
                      fontSize: 11,
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Telemetry log cards */}
              <Box
                sx={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  p: 1.5,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {filteredTelemetryEvents.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <SmartToyOutlined
                      sx={{ fontSize: 40, opacity: 0.4, mb: 1, color: "#724e41" }}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      No activities logged yet
                    </Typography>
                  </Box>
                ) : (
                  filteredTelemetryEvents.map((event) => (
                    <Paper
                      key={event.id}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? colors.primary[400]
                            : "#ffffff",
                        border: `1px solid ${theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.06)"
                          }`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700} fontSize={12}>
                          {event.title}
                        </Typography>
                        <Chip
                          label={event.status}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: 8.5,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            bgcolor:
                              event.status === "success"
                                ? "rgba(16, 185, 129, 0.15)"
                                : event.status === "running"
                                  ? "rgba(245, 158, 11, 0.15)"
                                  : "rgba(148, 163, 184, 0.15)",
                            color:
                              event.status === "success"
                                ? "#10b981"
                                : event.status === "running"
                                  ? "#f59e0b"
                                  : "#64748b",
                          }}
                        />
                      </Box>
                      {event.details && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontSize: 11, lineHeight: 1.4 }}
                        >
                          {event.details}
                        </Typography>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Floating Action Button (FAB) - hidden when chat is open */}
      {!isOpen && (
        <Tooltip title="Open Site Visit AI Chat" placement="left">
          <Fab
            variant="extended"
            onClick={() => setIsOpen(true)}
            sx={{
              px: 2.5,
              height: 48,
              borderRadius: 6,
              background: "linear-gradient(135deg, #724e41 0%, #8c5a45 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(114, 78, 65, 0.45)",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: "linear-gradient(135deg, #5c3b30 0%, #724e41 100%)",
                transform: "scale(1.05)",
                boxShadow: "0 12px 30px rgba(114, 78, 65, 0.6)",
              },
            }}
          >
            <AutoAwesome
              sx={{
                mr: 1.2,
                fontSize: 20,
                color: "#ffffff",
                animation: "pulse 2.2s infinite",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.15)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
            Site Visit AI
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default SiteVisitAiFloatingChat;
